import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getCarshareRegistrations, reviewCarshareReg } from '../api/admin';
import styles from './CarshareRegistrations.module.css';

// This is a Create React App project (react-scripts) — env vars are exposed
// via process.env.REACT_APP_*, not Vite's import.meta.env.VITE_* (that's the
// public-website's convention, not this app's). Set in admin-dashboard/.env.
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || '';

export default function CarshareRegistrations() {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['carshare-registrations'],
    queryFn: () => getCarshareRegistrations().then((r) => r.data),
  });

  const approveMutation = useMutation({
    mutationFn: (id) => reviewCarshareReg(id, { decision: 'approved' }),
    onSuccess: () => {
      toast.success('Registration approved');
      queryClient.invalidateQueries({ queryKey: ['carshare-registrations'] });
      setSelected(null);
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to approve'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, reason }) => reviewCarshareReg(id, { decision: 'rejected', rejection_reason: reason }),
    onSuccess: () => {
      toast.success('Registration rejected');
      queryClient.invalidateQueries({ queryKey: ['carshare-registrations'] });
      setSelected(null);
      setRejectionReason('');
    },
    onError: (err) => toast.error(err.response?.data?.error || 'Failed to reject'),
  });

  const regs = data?.registrations || [];

  const formatDate = (d) => d ? new Date(d).toLocaleString() : '—';

  if (isLoading) return <div className={styles.loading}>Loading…</div>;
  if (error) return <div className={styles.error}>Failed to load registrations.</div>;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Carsharing Registrations</h1>
      <p className={styles.subtitle}>Review one-time carsharing registration applications (license scan + selfie)</p>

      {regs.length === 0 ? (
        <div className={styles.empty}>
          <div className={styles.emptyIcon}>✅</div>
          <p>No pending registrations.</p>
        </div>
      ) : (
        <div className={styles.list}>
          {regs.map((reg) => {
            const u = reg.users || {};
            return (
              <div key={reg.id} className={styles.card} onClick={() => setSelected(reg)}>
                <div className={styles.cardHeader}>
                  <div className={styles.cardName}>{u.full_name || 'Unknown'}</div>
                  <div className={styles.cardStatus}>{(reg.status || 'pending').replace(/_/g, ' ')}</div>
                </div>
                <div className={styles.cardMeta}>
                  <span>{u.email}</span>
                  {u.phone && <span> · {u.phone}</span>}
                </div>
                <div className={styles.cardMeta}>
                  <span>Submitted: {formatDate(reg.created_at)}</span>
                </div>
                {reg.license_number && (
                  <div className={styles.cardMeta}>
                    <span>Licence #: {reg.license_number}</span>
                    {reg.license_category && <span> · Category: {reg.license_category}</span>}
                  </div>
                )}
                <div className={styles.cardMeta}>
                  <span>Liveness: {reg.liveness_passed ? '✅ Passed' : '❌ Not passed'}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {selected && (
        <div className={styles.overlay} onClick={() => setSelected(null)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>Review Registration</h2>
              <button className={styles.closeBtn} onClick={() => setSelected(null)}>✕</button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailRow}>
                <span>Name:</span>
                <span>{selected.users?.full_name || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Email:</span>
                <span>{selected.users?.email || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Phone:</span>
                <span>{selected.users?.phone || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Licence #:</span>
                <span>{selected.license_number || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Expiry:</span>
                <span>{selected.license_expiry || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Category:</span>
                <span>{selected.license_category || '—'}</span>
              </div>
              <div className={styles.detailRow}>
                <span>Liveness:</span>
                <span>{selected.liveness_passed ? '✅ Passed' : '❌ Not passed'}</span>
              </div>

              <div className={styles.photosSection}>
                <h3 className={styles.photosTitle}>Documents</h3>
                <div className={styles.photosGrid}>
                  <div className={styles.photoItem}>
                    <div className={styles.photoLabel}>Licence Front</div>
                    {selected.license_front_path ? (
                      <img src={`${SUPABASE_URL}/storage/v1/object/public/${selected.license_front_bucket}/${selected.license_front_path}`} alt="License front" className={styles.photo} />
                    ) : (
                      <div className={styles.photoPlaceholder}>No image</div>
                    )}
                  </div>
                  <div className={styles.photoItem}>
                    <div className={styles.photoLabel}>Licence Back</div>
                    {selected.license_back_path ? (
                      <img src={`${SUPABASE_URL}/storage/v1/object/public/${selected.license_back_bucket}/${selected.license_back_path}`} alt="License back" className={styles.photo} />
                    ) : (
                      <div className={styles.photoPlaceholder}>No image</div>
                    )}
                  </div>
                  <div className={styles.photoItem}>
                    <div className={styles.photoLabel}>Selfie</div>
                    {selected.selfie_path ? (
                      <img src={`${SUPABASE_URL}/storage/v1/object/public/${selected.selfie_bucket}/${selected.selfie_path}`} alt="Selfie" className={styles.photo} />
                    ) : (
                      <div className={styles.photoPlaceholder}>No image</div>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.rejectSection}>
                <label className={styles.rejectLabel}>Rejection reason (if rejecting):</label>
                <textarea
                  className={styles.rejectInput}
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Enter reason for rejection…"
                  rows={3}
                />
              </div>

              {selected?.status === 'pending' ? (
                <div className={styles.actions}>
                  <button
                    className={styles.approveBtn}
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(selected.id)}
                  >
                    {approveMutation.isPending ? 'Approving…' : '✅ Approve'}
                  </button>
                  <button
                    className={styles.rejectBtn}
                    disabled={rejectMutation.isPending}
                    onClick={() => rejectMutation.mutate({ id: selected.id, reason: rejectionReason })}
                  >
                    {rejectMutation.isPending ? 'Rejecting…' : '❌ Reject'}
                  </button>
                </div>
              ) : (
                <div className={styles.actions} style={{ justifyContent: 'center', color: '#64748b' }}>
                  No review actions available for {selected?.status} registrations.
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
